import { FC, useEffect, useRef, useState } from 'react';

import { IMAGES_PATH } from '@telialt/assets/src/constants';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { FacetTerms, SearchFacet, SearchFilters, SearchFilterValue } from '@telialt/core-search/src/model';
import { withParams } from '@telialt/utils/src/helpers';

import { CategoryData } from '../../../models';
import { ChipFilter } from '../ChipFilter/ChipFilter';
import { BrandFilterContainer, BrandFilterContent, BrandFilterCount, BrandImage } from './BrandFilters.styles';
import { getBrandIconFileName } from './constants';

export type BrandFiltersProps = {
    categoryData: CategoryData;
    facets: SearchFacet[];
    filters: SearchFilters;
    handleFilterChange: (fieldName: string, filter: SearchFilterValue) => void;
};

const BRAND_FIELD_NAME = 'brand';

export const BrandFilters: FC<BrandFiltersProps> = ({ categoryData, facets, filters, handleFilterChange }) => {
    const { t } = useTranslations();
    const warnedMissingBrandCache = useRef(new Set<string>());
    const [selectedBrands, setSelectedBrands] = useState<string[]>(() =>
        Array.isArray(filters.brand) ? filters.brand : [],
    );
    const filterChangeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        clearTimeout(filterChangeTimeoutRef.current);
        setSelectedBrands(Array.isArray(filters.brand) ? filters.brand : []);
    }, [filters.brand]);

    useEffect(() => () => clearTimeout(filterChangeTimeoutRef.current), []);

    const brands = categoryData.brands;

    if (!brands?.length) {
        return null;
    }

    const facetsBrands = (facets?.find((facet) => facet.key === 'brand' && facet.type === 'terms') as FacetTerms)
        ?.items;

    const matchedBrands = brands?.map((brand) => ({
        name: brand,
        count: facetsBrands?.find((facet) => facet.title === brand)?.count || 0,
    }));

    if (!matchedBrands.length) {
        return null;
    }

    const handleBrandFilterChange = (brand: string) => {
        const nextSelectedBrands = selectedBrands.includes(brand)
            ? selectedBrands.filter((b) => b !== brand)
            : [...selectedBrands, brand];

        setSelectedBrands(nextSelectedBrands);

        clearTimeout(filterChangeTimeoutRef.current);
        filterChangeTimeoutRef.current = setTimeout(() => {
            handleFilterChange(BRAND_FIELD_NAME, nextSelectedBrands);
        }, 0);
    };

    const brandsProps = matchedBrands.map((brand) => {
        const brandImgFilename = getBrandIconFileName(brand.name, warnedMissingBrandCache.current);
        const brandImgUrl = brandImgFilename
            ? `${IMAGES_PATH}/temp/brand-filter-icons/${brandImgFilename}?v=${process.env.GIT_HASH}`
            : undefined;
        const isSelected = selectedBrands.includes(brand.name);

        return {
            name: brand.name,
            count: brand.count,
            isDisabled: !brand.count,
            isSelected,
            onClick: () => handleBrandFilterChange(brand.name),
            imageSrc: brandImgUrl,
            imageAlt: withParams(t['eshop.category.brand_filter.image_alt'], {
                brand: brand.name,
                category: categoryData.name,
            }),
        };
    });

    return (
        <BrandFilterContainer>
            {brandsProps.map((brand) => (
                <ChipFilter
                    key={brand.name}
                    name="brands-filter"
                    checked={brand.isSelected}
                    disabled={brand.isDisabled}
                    value={brand.name}
                    onChange={brand.onClick}
                >
                    <BrandFilterContent>
                        <BrandImage src={brand.imageSrc} loading="lazy" alt={brand.imageAlt} />
                        <BrandFilterCount>({brand.count})</BrandFilterCount>
                    </BrandFilterContent>
                </ChipFilter>
            ))}
        </BrandFilterContainer>
    );
};
